import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { projects, projectOrder } from '../data/projects';
import { SEO } from '../components/SEO';

export function CaseStudiesPage() {
  const { currentLanguage } = useLanguage();

  const content = {
    en: { 
      eyebrow: 'Case Studies',
      title: 'Engineering Portfolio', 
      subtitle: 'Production-grade systems engineered for reliability, scale, and long-term operational stability.',
      viewProject: 'View Case Study',
      liveSite: 'Live Site'
    },
    tr: { 
      eyebrow: 'Referanslar',
      title: 'Mühendislik Portföyü', 
      subtitle: 'Güvenilirlik, ölçeklenebilirlik ve uzun vadeli operasyonel stabilite için tasarlanmış production seviye sistemler.',
      viewProject: 'Detayları Gör',
      liveSite: 'Canlı Site'
    },
    de: { 
      eyebrow: 'Fallstudien',
      title: 'Engineering-Portfolio', 
      subtitle: 'Production-grade Systeme für Zuverlässigkeit, Skalierbarkeit und langfristige Betriebsstabilität.',
      viewProject: 'Fallstudie ansehen',
      liveSite: 'Live-Site'
    }
  };

  const t = content[currentLanguage] || content.en;

  return (
    <>
      <SEO page="caseStudies" />
      <div className="min-h-screen">
        {/* Dark Hero Section */}
        <section 
          className="pt-32 pb-12 md:pt-36 md:pb-14 relative"
          style={{ 
            background: 'linear-gradient(180deg, #121821 0%, #0E1319 100%)',
            minHeight: '420px'
          }}
        >
          {/* Subtle grid pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          />
          
          <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8">
            <span 
              className="text-xs uppercase tracking-widest mb-4 block"
              style={{ color: '#14B8A6', letterSpacing: '0.15em' }}
            >
              {t.eyebrow}
            </span>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-4"
              style={{ color: '#FFFFFF' }}
              data-testid="case-studies-page-title"
            >
              {t.title}
            </h1>
            <p 
              className="text-base md:text-lg max-w-2xl mb-6"
              style={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              {t.subtitle}
            </p>
            {/* Curated note */}
            <p 
              className="text-xs uppercase tracking-wider"
              style={{ 
                color: 'rgba(255, 255, 255, 0.4)', 
                fontFamily: 'JetBrains Mono, monospace',
                letterSpacing: '0.1em'
              }}
            >
              {currentLanguage === 'tr' ? 'Datex tarafından mühendislik edilen seçkin sistemler.' : 
               currentLanguage === 'de' ? 'Ausgewählte Systeme, entwickelt von Datex.' :
               'Selected systems engineered by Datex.'}
            </p>
          </div>
        </section>

        {/* Light Content Section */}
        <div className="bg-white pb-20">
          {/* Subtle background */}
          <div 
            className="fixed inset-0 z-0 pointer-events-none opacity-40"
            style={{
              background: 'radial-gradient(ellipse at 50% 0%, rgba(15, 118, 110, 0.08) 0%, transparent 50%)'
            }}
          />

          <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 pt-16 md:pt-20">
          {/* Projects List */}
          <div className="space-y-20 md:space-y-24">
            {projectOrder.map((projectId, index) => {
              const project = projects[projectId];
              
              return (
                <article
                  key={projectId}
                  className="group relative"
                  data-testid={`case-study-card-${projectId}`}
                >
                  <Link
                    to={`/case-studies/${projectId}`}
                    data-testid={`case-study-link-${projectId}`}
                    className="block rounded-2xl overflow-hidden transition-all duration-500 hover:border-[#0F766E]"
                    style={{ 
                      background: 'linear-gradient(180deg, rgba(15, 118, 110, 0.08) 0%, rgba(15, 118, 110, 0.02) 100%)',
                      border: '1px solid rgba(15, 118, 110, 0.15)'
                    }}
                  >
                    <div className="p-6 md:p-8 lg:p-10">
                      {/* Top Row: Category & Year */}
                      <div className="flex items-center justify-between mb-6">
                        <span 
                          className="text-xs uppercase tracking-widest"
                          style={{ color: '#0F766E' }}
                        >
                          {project.category[currentLanguage] || project.category.en}
                        </span>
                        <span 
                          className="text-xs"
                          style={{ color: 'var(--text-on-light-subtle)', fontFamily: 'JetBrains Mono, monospace' }}
                        >
                          {project.year}
                        </span>
                      </div>

                      {/* Project Name */}
                      <h2 
                        className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight mb-3 group-hover:text-[#0F766E] transition-colors duration-300"
                        style={{ color: 'var(--text-on-light-primary)' }}
                      >
                        {project.name}
                      </h2>

                      {/* Tagline */}
                      <p 
                        className="text-base md:text-lg mb-8"
                        style={{ color: 'var(--text-on-light-muted)' }}
                      >
                        {project.tagline[currentLanguage] || project.tagline.en}
                      </p>

                      {/* Performance Metrics Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
                        {project.performance && Object.entries(project.performance).slice(0, 4).map(([key, item], idx) => (
                          <div key={key} className="text-center md:text-left">
                            <div 
                              className="text-xl md:text-2xl lg:text-3xl font-bold mb-1"
                              style={{ color: '#0F766E' }}
                            >
                              {item.value}
                            </div>
                            <div 
                              className="text-[10px] md:text-xs uppercase tracking-wider"
                              style={{ color: 'var(--text-on-light-muted)' }}
                            >
                              {item.label[currentLanguage] || item.label.en}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Bottom Row: Tech & CTA */}
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        {/* Tech Stack */}
                        <div className="flex flex-wrap gap-2">
                          {project.stack?.slice(0, 5).map((tech, idx) => (
                            <span 
                              key={idx}
                              className="px-3 py-1 text-xs rounded-full"
                              style={{ 
                                background: 'rgba(15, 118, 110, 0.1)',
                                border: '1px solid rgba(15, 118, 110, 0.15)',
                                color: 'var(--text-on-light-muted)',
                              }}
                            >
                              {tech}
                            </span>
                          ))}
                        </div>

                        {/* CTAs */}
                        <div className="flex items-center gap-4">
                          {/* Live Site Link */}
                          {project.link && (
                            <a
                              href={project.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300 hover:gap-3"
                              style={{ 
                                background: 'rgba(15, 118, 110, 0.1)',
                                border: '1px solid rgba(15, 118, 110, 0.2)',
                                color: '#0F766E'
                              }}
                            >
                              <ExternalLink className="w-4 h-4" />
                              {t.liveSite}
                            </a>
                          )}
                          
                          {/* View Details */}
                          <div 
                            className="flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all duration-300"
                            style={{ color: 'var(--text-on-light-muted)' }}
                          >
                            {t.viewProject}
                            <ArrowRight className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>

                </article>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 md:mt-20 text-center">
            <p 
              className="text-sm mb-4"
              style={{ color: 'var(--text-on-light-muted)' }}
            >
              {currentLanguage === 'tr' ? 'Projenizi tartışmaya hazır mısınız?' : 
               currentLanguage === 'de' ? 'Bereit, Ihr Projekt zu besprechen?' :
               'Ready to discuss your project?'}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-300 hover:gap-3 hover:opacity-90"
              style={{ 
                background: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
                color: '#FFFFFF',
                border: '1px solid rgba(15, 118, 110, 0.3)'
              }}
            >
              {currentLanguage === 'tr' ? 'İletişime Geçin' : 
               currentLanguage === 'de' ? 'Kontakt aufnehmen' :
               'Get in Touch'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          </div>
        </div>
      </div>
    </>
  );
}
